import React, { useCallback } from "react";
import { StateContext } from "../context/globalContext/context";
import { GA_EVENTS, GA_LABEL_ENUMS, getCurrentUserEmail, triggerGAEvent } from "../constant/google-analytics/GAEvents";
import { getItemFromSessionStorage, setItemInSessionStorage } from "./sessionStorage";
import { getSessionId } from "../utils/sessionId";
import { environment } from "../constant/environment";
import { ENVIRONMENT_TYPE } from "../constant/common";
import { decryptData } from "../utils/api/encryption";
import { extractParamFromUrl } from "../utils/trackingParams";

// Helper function to get and cache page referrer
const getPageReferrer = (): string => {
  // Check if referrer is already cached in sessionStorage
  const cachedReferrer = getItemFromSessionStorage("_zenvoya_page_referrer");
  if (cachedReferrer) {
    return cachedReferrer;
  }

  // Get the current referrer from document
  const currentReferrer = document.referrer || "";

  // Cache it for future use
  if (currentReferrer) {
    setItemInSessionStorage("_zenvoya_page_referrer", currentReferrer);
    console.log("Page referrer cached:", currentReferrer);
  }

  return currentReferrer;
};

// Helper to read and cache _fprom_tid from FirstPromoter cookie
const getFpromTid = (): string => {
  const cached = getItemFromSessionStorage("_fprom_tid");
  if (cached) return cached;

  const cookie = document.cookie.split("; ").find((row) => row.startsWith("_fprom_tid="));
  const value = cookie ? cookie.split("=")[1] : "";

  if (value) {
    setItemInSessionStorage("_fprom_tid", value);
  }

  return value;
};

export const usePixelEvent = () => {
  const state = React.useContext(StateContext);

  const {
    pixelEventState,
    setPixelEventState,
    utmParams,
    userLocation,
    fbpIdLocal,
    setFbpId,
    fbcIdLocal,
    setFbcId,
  } = state;
  const creationTime = Date.now();
  const randomNumber = Math.random().toString(36).substring(2, 15);
  const fbpid = fbpIdLocal || `fb.1.${creationTime}.${randomNumber}`;
  if (!fbpIdLocal) {
    setFbpId(fbpid);
  }

  const searchParams = new URLSearchParams(utmParams);
  // Read fbclid via regex over a recursively-decoded URL so double-encoded
  // landings (e.g. `?https%3A%2F%2Fzenvoya.ai%3Ffbclid%3D...`) are covered —
  // URLSearchParams/searchParams.get can't see params buried in encoded values.
  const fbclid =
    searchParams.get("fbclid") ||
    extractParamFromUrl(window.location.href, "fbclid") ||
    "";

  // Initialize fbcId from fbclid if present and not already set
  if (fbclid && !fbcIdLocal) {
    const fbcValue = `fb.1.${Date.now()}.${fbclid}`;
    setFbcId(fbcValue);
  }

  const updatePixelEventState = useCallback(
    (eventType: any, value: boolean) => {
      setPixelEventState((prev: any) => ({
        ...prev,
        [eventType]: value,
      }));
    },
    [setPixelEventState]
  );

  // const hashData = async (data: string): Promise<string> => {
  //   const encoder = new TextEncoder();
  //   const dataBuffer = encoder.encode(data.toLowerCase().trim());
  //   const hashBuffer = await crypto.subtle.digest("SHA-256", dataBuffer);
  //   const hashArray = Array.from(new Uint8Array(hashBuffer));
  //   return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  // };

  const getUserData = useCallback(async (): Promise<any> => {
    const userData: any = {
      client_ip_address: userLocation?.ip || "",
      client_user_agent: navigator.userAgent,
      fbp: fbpid,
    };

    // Use fbcIdLocal from state if available
    if (fbcIdLocal) {
      userData.fbc = fbcIdLocal;
    } else {
      // Fallback: check URL params (regex-based to handle double-encoded URLs)
      const fbclidFromUrl = extractParamFromUrl(window.location.href, "fbclid");

      if (fbclidFromUrl) {
        const fbcValue = `fb.1.${Date.now()}.${fbclidFromUrl}`;
        userData.fbc = fbcValue;
        setFbcId(fbcValue);
      } else if (fbclid) {
        const fbcValue = `fb.1.${Date.now()}.${fbclid}`;
        userData.fbc = fbcValue;
        setFbcId(fbcValue);
      }
    }

    return userData;
  }, [fbpid, fbcIdLocal, fbclid, setFbcId, userLocation?.ip]);

  const sendPixelEvent = useCallback(
    async (eventName: string, parameters = {}) => {
      try {
        const fbq = (window as any).fbq;
        if (!fbq) {
          console.warn("Facebook Pixel not available - Event not sent:", eventName);
          return false;
        }

        const eventStateMap: any = {
          appLoaded: "pageLoadPixelEventSent",
          signUp: "signUpPixelEventSent",
          startChat: "startChatPixelEventSent",
          viewItinerary: "viewItineraryPixelEventSent",
          searchFlights: "searchFlightsPixelEventSent",
          searchStays: "searchStaysPixelEventSent",
          selectFlights: "selectFlightsPixelEventSent",
          selectStays: "selectStaysPixelEventSent",
          reviewCart: "reviewCartPixelEventSent",
          interactedWithChat: "interactedWithChatPixelEventSent",
          login: "loginPixelEventSent",
        };

        const stateKey = eventStateMap[eventName];

        if (stateKey && pixelEventState[stateKey]) {
          console.log(`${eventName} pixel event already sent - skipping`);
          return false;
        }

        const userData = await getUserData();

        const payload = {
          data: [
            {
              event_name: eventName,
              event_time: Math.floor(Date.now() / 1000),
              action_source: "website",
              event_source_url: window.location.href,
              user_data: userData,
              custom_data: {
                ...parameters,
                event_id: `${eventName}_${Date.now()}_${Math.random()}`, // Prevent duplicate events
              },
            },
          ],
        };

        console.log("Sending pixel event via API:", eventName, payload);
        // await fetchApi(accessJWT, endPoints.facebookPixel, API_METHODS.POST, {
        //   eventName: eventName,
        //   eventData: payload,
        //   bypassJWTValidation: true,
        // });

        // Send comprehensive data to Lambda API
        try {
          // Get geolocation (cached)
          // const getGeolocation = async () => {
          //   const cachedGeolocation = getItemFromLocalStorage("_zenvoya_geolocation");
          //   if (cachedGeolocation) {
          //     return cachedGeolocation;
          //   }
          //   try {
          //     const response = await fetchApi(accessJWT, endPoints.geolocation, API_METHODS.POST, {});
          //     if (response && response.data) {
          //       const geolocationData = response?.data?.fullData?.location || {};
          //       geolocationData.ipv4 = response?.data?.fullData?.ip || "";
          //       setItemInLocalStorage("_zenvoya_geolocation", geolocationData);
          //       return geolocationData;
          //     }
          //   } catch (error) {
          //     console.error("Error fetching geolocation:", error);
          //   }
          //   return null;
          // };

          // Get GA session info (cached)
          const getGASessionInfo = () => {
            const customSessionId = getSessionId();

            const cachedSessionInfo = getItemFromSessionStorage("_zenvoya_ga_session_info");
            if (cachedSessionInfo) {
              if (!cachedSessionInfo.session_id) {
                cachedSessionInfo.session_id = customSessionId;
                setItemInSessionStorage("_zenvoya_ga_session_info", cachedSessionInfo);
              }
              return cachedSessionInfo;
            }

            try {
              const gaCookie = document.cookie.split("; ").find((row) => row.startsWith("_ga="));
              if (gaCookie) {
                const gaValue = gaCookie.split("=")[1];
                const parts = gaValue.split(".");
                if (parts.length >= 4) {
                  const sessionId = parseInt(parts[3]) || Date.now();
                  const gaSessionCookie = document.cookie.split("; ").find((row) => row.startsWith("_ga_"));
                  let sessionNumber = 1;
                  if (gaSessionCookie) {
                    const sessionValue = gaSessionCookie.split("=")[1];
                    const sessionParts = sessionValue.split(".");
                    if (sessionParts.length >= 4) {
                      sessionNumber = parseInt(sessionParts[3]) || 1;
                    }
                  }
                  const sessionInfo = {
                    ga_session_id: sessionId,
                    ga_session_number: sessionNumber,
                    session_id: customSessionId,
                  };
                  setItemInSessionStorage("_zenvoya_ga_session_info", sessionInfo);
                  return sessionInfo;
                }
              }
            } catch (e) {
              console.error("Error extracting GA session info:", e);
            }

            const fallbackSessionInfo = {
              ga_session_id: Date.now(),
              ga_session_number: 1,
              session_id: customSessionId,
            };
            setItemInSessionStorage("_zenvoya_ga_session_info", fallbackSessionInfo);
            return fallbackSessionInfo;
          };

          // Get user info
          const getUserInfo = () => {
            const userObj: any = {};
            try {
              // Check if user_pseudo_id is already cached in sessionStorage
              const cachedUserPseudoId = getItemFromSessionStorage("_zenvoya_user_pseudo_id");
              if (cachedUserPseudoId) {
                userObj.user_pseudo_id = cachedUserPseudoId;
              } else {
                // Try to extract from _ga cookie
                const gaCookie = document.cookie.split("; ").find((row) => row.startsWith("_ga="));
                if (gaCookie) {
                  const gaValue = gaCookie.split("=")[1];
                  const parts = gaValue.split(".");
                  if (parts.length >= 4) {
                    const userPseudoId = `${parts[2]}.${parts[3]}`;
                    userObj.user_pseudo_id = userPseudoId;
                    // Cache the user_pseudo_id for future use
                    setItemInSessionStorage("_zenvoya_user_pseudo_id", userPseudoId);
                    console.log("User pseudo ID extracted and cached:", userPseudoId);
                  }
                } else {
                  console.log("GA cookie not yet available, user_pseudo_id will be missing");
                }
              }
            } catch (e) {
              console.error("Error extracting GA Client ID:", e);
            }

            try {
              const userInfoStr = localStorage.getItem("user_info");
              if (userInfoStr) {
                if (environment === ENVIRONMENT_TYPE.LOCAL) {
                  const userInfo = JSON.parse(userInfoStr);
                  if (userInfo && userInfo.email) {
                    userObj.email = userInfo.email;
                  }
                } else {
                  const userInfo = decryptData(userInfoStr);
                  if (userInfo && userInfo.email) {
                    userObj.email = userInfo.email;
                  }
                }
              }
            } catch (e) {
              console.error("Error extracting user email:", e);
            }

            // Fallback to the in-memory cache kept in sync with StateContext.userInfo
            // by React — localStorage reads can be stale/empty at the moment an event fires.
            const cachedEmail = getCurrentUserEmail();
            if (!userObj.email && cachedEmail) {
              userObj.email = cachedEmail;
            }

            return Object.keys(userObj).length > 0 ? userObj : null;
          };

          // Determine app identifier
          const hostname = window.location.hostname;
          let app = "app-dev.zenvoya.ai";
          if (hostname.includes("app-dev")) {
            app = "app-dev.zenvoya.ai";
          } else if (hostname.includes("app-prompt")) {
            app = "app-prompt.zenvoya.ai";
          } else if (hostname.includes("app-patient")) {
            app = "app-pat.zenvoya.ai";
          } else if (hostname.includes("app-booking-test")) {
            app = "app-booking-test.zenvoya.ai";
          } else if (hostname.includes("chat")) {
            app = "chat.zenvoya.ai";
          }

          // const geoLocation = await getGeolocation();
          const sessionInfo = getGASessionInfo();
          const userInfo = getUserInfo();
          const { label = "" } = GA_EVENTS[eventName] || {};

          const lambdaPayload: any = {
            event_name: label,
            sent_at: Math.floor(Date.now() * 1000),
            event_source_url: window.location.href,
            custom: {
              ...parameters,
            },
            max_touch_points: navigator.maxTouchPoints || 0,
            context: {
              app: app,
              page_location: window.location.href,
              page_referrer: getPageReferrer(),
              page_title: document.title || "",
              page_path: window.location.pathname,
              _fbc: userData.fbc || getItemFromSessionStorage("fbcId") || "",
              _gclid: getItemFromSessionStorage("gclid") || "",
              _gbraid: getItemFromSessionStorage("gbraid") || "",
              _fbp:
                document.cookie
                  .split("; ")
                  .find((row) => row.startsWith("_fbp="))
                  ?.split("=")[1] || "",
              browser_id:
                document.cookie
                  .split("; ")
                  .find((row) => row.startsWith("_fbp="))
                  ?.split("=")[1] || "",
              _fprom_tid: getFpromTid(),
            },
            session: sessionInfo,
            // geolocation: geoLocation,
            env: environment === "production" ? "PROD" : "DEV",
            type: "lambda",
          };

          if (userInfo) {
            lambdaPayload.user = userInfo;
          }

          // Remove empty keys before sending
          // const cleanedPayload = removeEmptyKeys(lambdaPayload);

          // console.log("Sending comprehensive event to Lambda API:", eventName, cleanedPayload);
          // await fetchApi(accessJWT, endPoints.facebookPixel, API_METHODS.POST, {
          //   eventName: label,
          //   eventData: cleanedPayload,
          //   // geoLocation: geoLocation,
          // });
        } catch (error) {
          console.error("Failed to send Lambda event:", error);
        }

        // console.log("Sending pixel event:", eventName);
        // fbq("trackCustom", eventName, parameters);

        const eventMap: any = {
          appLoaded: GA_LABEL_ENUMS.appLoaded,
          signUp: GA_LABEL_ENUMS.signUp,
          startChat: GA_LABEL_ENUMS.startChat,
          viewItinerary: GA_LABEL_ENUMS.viewItinerary,
          searchFlights: GA_LABEL_ENUMS.searchFlights,
          searchStays: GA_LABEL_ENUMS.searchStays,
          selectFlights: GA_LABEL_ENUMS.selectFlights,
          selectStays: GA_LABEL_ENUMS.selectStays,
          reviewCart: GA_LABEL_ENUMS.reviewCart,
          promoCode: GA_LABEL_ENUMS.promoCode,
          paymentComplete: GA_LABEL_ENUMS.paymentComplete,
          login: GA_LABEL_ENUMS.login,
          interactedWithChat: GA_LABEL_ENUMS.interactedWithChat,
        };

        if (eventMap[eventName]) {
          triggerGAEvent(eventMap[eventName], "", "", "", parameters);
          console.log("Triggered GA event for:", eventMap[eventName]);
        }

        if (stateKey) {
          updatePixelEventState(stateKey, true);
        }

        return true;
      } catch (error) {
        console.error("Failed to send pixel event:", eventName, error);
        return false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pixelEventState, updatePixelEventState]
  );

  return {
    sendPixelEvent,
    pixelEventState,
  };
};
